import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import AuthService, { SignupData, RequiresPasswordReset } from '../services/AuthService';

interface User {
  userId: string;
  email: string;
  firstName: string;
  lastName: string;
  groups: string[];
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  signIn: (username: string, password: string) => Promise<RequiresPasswordReset | undefined>;
  signUp: (data: SignupData) => Promise<void>;
  completePasswordMigration: (username: string, newPassword: string, confirmPassword: string) => Promise<void>;
  signOut: () => void;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is already logged in
    const currentUser = AuthService.getCurrentUser();
    if (currentUser && AuthService.isAuthenticated()) {
      setUser(currentUser);
    }
    setLoading(false);

    // AuthService.logout() fires this whenever a token refresh fails, not
    // just on an explicit sign-out click (see its own comment) -- without
    // this listener, this state would keep believing the session is live
    // after localStorage was already cleared underneath it.
    const handleAuthLogout = () => setUser(null);
    window.addEventListener('auth:logout', handleAuthLogout);

    // Set up token refresh timer
    const refreshInterval = setInterval(async () => {
      if (AuthService.isAuthenticated()) {
        try {
          await AuthService.refreshAccessToken();
        } catch (error) {
          // AuthService.refreshAccessToken() already calls logout() on
          // failure, which dispatches 'auth:logout' above.
        }
      }
    }, 50 * 60 * 1000); // Refresh every 50 minutes

    return () => {
      window.removeEventListener('auth:logout', handleAuthLogout);
      clearInterval(refreshInterval);
    };
  }, []);

  const signIn = async (username: string, password: string) => {
    const result = await AuthService.login({ username, password });
    if ('requiresPasswordReset' in result) {
      return result;
    }
    setUser(result.user);
    return undefined;
  };

  const signUp = async (data: SignupData) => {
    const result = await AuthService.signup(data);
    setUser(result.user);
  };

  const completePasswordMigration = async (username: string, newPassword: string, confirmPassword: string) => {
    const result = await AuthService.migratePassword(username, newPassword, confirmPassword);
    setUser(result.user);
  };

  const signOut = () => {
    AuthService.logout();
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      signIn,
      signUp,
      completePasswordMigration,
      signOut,
      isAuthenticated: !!user
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
